import React, { useEffect, useMemo, useState } from "react";
import { Package, ShoppingBag, Truck, CheckCircle2, Clock, X, PackageCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/time";
import { Label, StatCard, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";

const money = (n) => `R${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_UI = {
  pending_payment: { label: "Awaiting EFT", color: "bg-amber-100 text-amber-800", Icon: Clock },
  paid: { label: "Payment received", color: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  in_production: { label: "In production", color: "bg-indigo-100 text-indigo-800", Icon: Package },
  shipped: { label: "Shipped", color: "bg-sky-100 text-sky-800", Icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800", Icon: PackageCheck },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-500", Icon: X },
};

const NEXT = {
  pending_payment: ["paid", "cancelled"],
  paid: ["in_production", "cancelled"],
  in_production: ["shipped", "cancelled"],
  shipped: ["delivered"],
  delivered: [],
  cancelled: [],
};

function Chip({ status }) {
  const ui = STATUS_UI[status] || STATUS_UI.pending_payment;
  const Icon = ui.Icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${ui.color}`} data-testid={`status-${status}`}><Icon className="h-3.5 w-3.5" />{ui.label}</span>;
}

function TransitionDialog({ order, onClose, onSaved }) {
  const [status, setStatus] = useState("");
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const options = order ? NEXT[order.status] || [] : [];
  useEffect(() => { setStatus(options[0] || ""); setCourier(""); setTracking(""); setNote(""); }, [order?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!status) return;
    if (status === "shipped" && (!courier.trim() || !tracking.trim())) {
      toast.error("Courier and tracking number are required for 'shipped'"); return;
    }
    setBusy(true);
    try {
      const body = { status, note: note || null };
      if (status === "shipped") { body.courier = courier.trim(); body.tracking_number = tracking.trim(); }
      const { data } = await api.patch(`/platform/merchandise/orders/${order.id}`, body);
      toast.success("Order updated — tenant notified");
      onSaved(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="transition-dialog">
        <DialogHeader><DialogTitle>Update {order?.number}</DialogTitle></DialogHeader>
        {!options.length ? <p className="py-6 text-center text-sm text-slate-500">This order is at a terminal status and can't be moved further.</p> : (
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Move to</p>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="transition-status"><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{STATUS_UI[o].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {status === "shipped" && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div><p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Courier *</p>
                  <Input value={courier} onChange={(e) => setCourier(e.target.value)} data-testid="transition-courier" placeholder="e.g. RAM Hand-to-Hand" /></div>
                <div><p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Tracking # *</p>
                  <Input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="transition-tracking" /></div>
              </div>
            )}
            <div><p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Internal / customer note (optional)</p>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="transition-note" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {options.length > 0 && <Button onClick={save} disabled={busy} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="transition-save">{busy ? "Saving…" : "Save & notify tenant"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDrawer({ order, onClose, onEdit }) {
  if (!order) return null;
  const addr = order.shipping_address || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/40" onClick={onClose} data-testid="order-drawer">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{order.organization_name}</p>
            <h2 className="font-head text-2xl font-bold text-slate-900">{order.number}</h2>
            <div className="mt-2"><Chip status={order.status} /></div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 space-y-5">
          <div className="rounded-xl bg-slate-50 p-4 text-sm">
            <p className="font-head font-bold text-slate-900">Ship to</p>
            <p className="mt-1 text-slate-700">{addr.recipient}{addr.company ? ` · ${addr.company}` : ""}</p>
            <p className="text-slate-600">{addr.street}, {addr.city} {addr.postal_code}, {addr.country}</p>
            {addr.phone && <p className="text-slate-600">{addr.phone}</p>}
            {addr.delivery_note && <p className="mt-2 italic text-slate-600">"{addr.delivery_note}"</p>}
          </div>
          <div className="rounded-xl border border-slate-200 p-4 text-sm">
            <p className="font-head font-bold text-slate-900">Items</p>
            <div className="mt-2 divide-y divide-slate-100">
              {order.items.map((it) => (
                <div key={`${it.variant_sku}-${it.employee_id || "x"}`} className="flex justify-between gap-3 py-2">
                  <span className="flex-1 text-slate-700">{it.quantity}× {it.product_name}<span className="block text-xs text-slate-400">{it.variant_sku} · {it.variant_name}{it.display_name ? ` · ${it.display_name}` : ""}</span></span>
                  <span className="font-head font-bold text-slate-900">{money(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Bulk discount</span><span>−{money(order.discount)}</span></div>}
              <div className="flex justify-between text-slate-600"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : money(order.shipping)}</span></div>
              <div className="flex justify-between font-head font-bold text-slate-900"><span>Total</span><span>{money(order.total)}</span></div>
            </div>
          </div>
          <div>
            <p className="font-head font-bold text-slate-900">Timeline</p>
            <ol className="mt-2 space-y-2 text-sm">
              {(order.status_history || []).map((h) => {
                const ui = STATUS_UI[h.status] || STATUS_UI.pending_payment;
                const Icon = ui.Icon;
                return (
                  <li key={`${h.at}-${h.status}`} className="flex items-start gap-3">
                    <span className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${ui.color}`}><Icon className="h-4 w-4" /></span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{ui.label}</p>
                      <p className="text-xs text-slate-500">{fmtDateTime(h.at)} · {h.by}</p>
                      {h.note && <p className="mt-1 text-xs text-slate-600">{h.note}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
          <Button onClick={() => onEdit(order)} data-testid="order-advance" className="w-full bg-[#4338CA] hover:bg-[#3730A3]">Advance status</Button>
        </div>
      </div>
    </div>
  );
}

export default function PlatformMerchandise() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("open");
  const [drawer, setDrawer] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/platform/merchandise").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!d) return [];
    if (filter === "all") return d.orders;
    if (filter === "open") return d.orders.filter((o) => !["delivered", "cancelled"].includes(o.status));
    return d.orders.filter((o) => o.status === filter);
  }, [d, filter]);

  const afterSave = (updated) => {
    setD((prev) => prev ? { ...prev, orders: prev.orders.map((o) => o.id === updated.id ? { ...o, ...updated } : o) } : prev);
    setDrawer((prev) => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
    load();
  };

  if (!d) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6">
      <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Merchandise Pipeline</h1>
        <p className="mt-1 text-sm text-slate-500">Track and fulfil physical hardware orders across all tenants.</p></div>

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Merch Revenue" value={money(d.revenue).replace("R", "R ")} accent="#10B981" testId="merch-revenue" />
        <StatCard label="Orders" value={d.order_count} testId="merch-orders"><ShoppingBag className="h-5 w-5 text-slate-300" /></StatCard>
        <StatCard label="Units" value={d.units} testId="merch-units"><Package className="h-5 w-5 text-slate-300" /></StatCard>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Label>Orders across organisations</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="merch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (not delivered/cancelled)</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending_payment">Awaiting EFT</SelectItem>
              <SelectItem value="paid">Payment received</SelectItem>
              <SelectItem value="in_production">In production</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? <div className="mt-4"><EmptyState icon={Package} title="No orders to show" /></div> : (
          <div className="mt-4 overflow-x-auto sv-scroll">
            <table className="w-full min-w-[760px] text-sm" data-testid="merch-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2">Order</th><th>Organisation</th><th>Items</th><th>Total</th><th>Status</th><th>Placed</th><th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`merch-order-${o.id}`}>
                    <td className="py-3 font-head font-bold text-slate-800">{o.number}</td>
                    <td className="text-slate-700">{o.organization_name}</td>
                    <td className="text-slate-500">{o.items.length}</td>
                    <td className="sv-metric font-bold text-slate-800">{money(o.total)}</td>
                    <td><Chip status={o.status} /></td>
                    <td className="text-slate-400">{fmtDateTime(o.created_at)}</td>
                    <td className="pr-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setDrawer(o)} data-testid={`merch-open-${o.id}`}>Open <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OrderDrawer order={drawer} onClose={() => setDrawer(null)} onEdit={setEditing} />
      <TransitionDialog order={editing} onClose={() => setEditing(null)} onSaved={afterSave} />
    </div>
  );
}
